const decimalPattern = /^-?\d+(?:\.\d+)?$/u;

interface FinancialAmountProps {
  currency: string;
  locale?: string;
  value: string | null | undefined;
}

interface PercentageProps {
  locale?: string;
  value: string | null | undefined;
}

interface DateValueProps {
  locale?: string;
  value: string | null | undefined;
}

export function FinancialAmount({ currency, locale = "en-US", value }: FinancialAmountProps) {
  return <span>{formatFinancialAmount(value, currency, locale)}</span>;
}

export function Percentage({ locale = "en-US", value }: PercentageProps) {
  return <span>{formatDecimal(value, locale, "%", 4)}</span>;
}

export function RatePercentage({ locale = "en-US", value }: PercentageProps) {
  return <span>{formatPercentage(decimalRateToPercentage(value), locale)}</span>;
}

export function DateValue({ locale = "en-US", value }: DateValueProps) {
  return <time dateTime={value ?? undefined}>{formatDate(value, locale)}</time>;
}

export function formatFinancialAmount(
  value: string | null | undefined,
  currency: string,
  locale = "en-US",
): string {
  if (!value || !decimalPattern.test(value) || !/^[A-Z]{3}$/u.test(currency)) return "—";

  const currencyParts = new Intl.NumberFormat(locale, {
    currency,
    currencyDisplay: "symbol",
    style: "currency",
  }).formatToParts(0);
  const symbol = currencyParts.find((part) => part.type === "currency")?.value ?? currency;
  const symbolBefore = currencyParts.findIndex((part) => part.type === "currency") <
    currencyParts.findIndex((part) => part.type === "integer");
  const formatted = formatDecimal(value, locale, "", 2, 2);

  return symbolBefore ? `${symbol}${formatted}` : `${formatted} ${symbol}`;
}

export function formatPercentage(value: string | null | undefined, locale = "en-US"): string {
  return formatDecimal(value, locale, "%", 4);
}

export function formatRatePercentage(value:string|null|undefined,locale="en-US"):string {
  return formatPercentage(decimalRateToPercentage(value),locale);
}

export function formatDate(value: string | null | undefined, _locale = "en-US"): string {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/u);
  if (!match) return "—";

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "—";
  }

  return `${month}/${day}/${year}`;
}

function formatDecimal(value: string | null | undefined, locale: string, suffix = "", maximumFractionDigits?: number, minimumFractionDigits = 0): string {
  const match = value?.match(/^(-?)(\d+)(?:\.(\d+))?$/u);
  if (!match) return "—";

  const rounded = roundDecimalDigits(match[2]!, match[3] ?? "", maximumFractionDigits);
  const separators = new Intl.NumberFormat(locale).formatToParts(1000.1);
  const group = separators.find((part) => part.type === "group")?.value ?? ",";
  const decimal = separators.find((part) => part.type === "decimal")?.value ?? ".";
  const negative = match[1] === "-";
  const integer = rounded.integer;
  const fraction = rounded.fraction.replace(/0+$/u, "").padEnd(minimumFractionDigits, "0");
  const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/gu, group);

  return `${negative ? "-" : ""}${groupedInteger}${fraction ? `${decimal}${fraction}` : ""}${suffix}`;
}

function roundDecimalDigits(integer:string,fraction:string,maximumFractionDigits?:number):{integer:string;fraction:string}{
  if(maximumFractionDigits===undefined||fraction.length<=maximumFractionDigits)return{integer,fraction};
  const kept=fraction.slice(0,maximumFractionDigits);
  if(Number(fraction[maximumFractionDigits])<5)return{integer,fraction:kept};
  const scale=10n**BigInt(maximumFractionDigits);
  const rounded=BigInt(integer)*scale+BigInt(kept||"0")+1n;
  return{integer:String(rounded/scale),fraction:maximumFractionDigits===0?"":String(rounded%scale).padStart(maximumFractionDigits,"0")};
}

function decimalRateToPercentage(value:string|null|undefined):string|null {
  const match=value?.match(/^(-?)(\d+)(?:\.(\d+))?$/u);
  if(!match)return null;
  const fraction=(match[3]??"").padEnd(2,"0");
  const digits=`${match[2]}${fraction}`.padEnd(match[2]!.length+2,"0");
  const split=match[2]!.length+2;
  const integer=String(BigInt(digits.slice(0,split)));
  const remainder=digits.slice(split);
  return `${match[1]}${integer}${remainder?`.${remainder}`:""}`;
}